/**
 * Template renderer — recursively walks the AST produced by the parser and
 * emits an HTML string.  Async because partial ({{> name}}) resolution may
 * trigger network loads on cache-miss.
 */

/**
 * Converts a resolved value to a safe string for HTML output.
 * Arrays / objects produce an empty string — use BEGIN/END blocks for those.
 *
 * @param {*} value - Any resolved value.
 * @returns {string} String-safe representation.
 */
function stringifyValue(value) {
    if (value === null || value === undefined) return ""
    if (typeof value === "string") return value
    if (typeof value === "number" || typeof value === "boolean") return String(value)
    // Objects / arrays should not be interpolated directly
    return ""
}

/**
 * Walks an AST node tree and produces the final HTML string.
 *
 * @param {Array<Object>} nodes   - AST nodes from the parser.
 * @param {TemplateContext} context - Current scope context.
 * @param {Object} engine          - The TemplateEngine instance (needed for partial resolution).
 * @returns {Promise<string>} Rendered HTML.
 */
export async function renderNodes(nodes, context, engine) {
    let output = ""

    for (const node of nodes) {
        switch (node.type) {
            // ── Raw text ────────────────────────────────────────────────
            case "text":
                output += node.value
                break

            // ── {{variable}} ────────────────────────────────────────────
            case "variable":
                output += stringifyValue(context.resolve(node.path))
                break

            // ── {{t "text"}} ────────────────────────────────────────────
            case "translate":
                output += engine.translate ? engine.translate(node.text) : node.text
                break

            // ── {{> partial}} ───────────────────────────────────────────
            case "partial": {
                const partialAST = await engine.loadAndParse(node.name)
                output += await renderNodes(partialAST, context, engine)
                break
            }

            // ── <!-- BEGIN: name --> … <!-- END: name --> ───────────────
            case "block": {
                const items = context.resolve([node.name])

                if (!Array.isArray(items)) break

                for (let i = 0; i < items.length; i++) {
                    const childCtx = context.createChild(items[i], {
                        _index: i,
                        _count: items.length,
                        _first: i === 0,
                        _last: i === items.length - 1,
                    })
                    output += await renderNodes(node.children, childCtx, engine)
                }
                break
            }

            // ── <!-- IF: name --> … <!-- ELSE: name --> … <!-- ENDIF: name -->
            case "conditional": {
                if (context.isTruthy(node.path)) {
                    output += await renderNodes(node.children, context, engine)
                } else if (node.elseChildren) {
                    output += await renderNodes(node.elseChildren, context, engine)
                }
                break
            }

            default:
                break
        }
    }

    return output
}
